import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'
import { patientObj } from '../PatientChartPage/PatientChartPage.provider'
import { AccessRoleList } from '../ProvidersProvider/types'
import { Patient, PatientList } from './types'

export interface PatientsContextInterface {
  patients: PatientList | null
  myPatients: PatientList | null
  myActivePatients: PatientList
  allActivePatients: PatientList
  allDeactivatedPatients: PatientList
  myDeactivatedPatients: PatientList
  getPatients: () => Promise<any>
  getMyPatients: () => Promise<any>
  deactivatePatient: (
    patientMRN: string,
    deactivationDate: Date
  ) => Promise<any>
  reactivatePatient: (patientMRN: string) => Promise<any>
  patientAccessRoles: AccessRoleList | null
  updatePatientAccessRole: (
    patientId: number,
    accessRoleId: number
  ) => Promise<void>
  getListOfAllActivePatients: () => Promise<any>
  getListOfMyActivePatients: () => Promise<any>
  getListOfAllDeactivatedPatients: () => Promise<any>
  getListOfMyDeactivatedPatients: () => Promise<any>
  isResultLoading: boolean
}

// EXPORT CONTEXT NEW
export const PatientsContext = createContext<PatientsContextInterface | null>(
  null
)

// NEW PATIENTS PROVIDER
export const PatientsProvider = ({ children }: { children?: ReactNode }) => {
  const [patients, setPatients] = useState(null)
  const [myPatients, setMyPatients] = useState(null)

  const [patientAccessRoles, setPatientAccessRoles] = useState(null)
  const [allActivePatients, setAllActivePatients] = useState([])
  const [myActivePatients, setMyActivePatients] = useState([])
  const [allDeactivatedPatients, setAllDeactivatedPatients] = useState([])
  const [myDeactivatedPatients, setMyDeactivatedPatients] = useState([])

  const [isResultLoading, setIsResultLoading] = useState(false)

  const { fetch } = useAxios()

  const getPatients = useCallback(async () => {
    const { data } = await fetch({
      path: 'Provider/GetListOfPatients', // look at network tab to see the results
    })
    if (data) {
      setPatients(data)
    }
  }, [])

  const getMyPatients = useCallback(async () => {
    const { data } = await fetch({
      path: 'Provider/GetListOfMyPatients', // look at network tab to see the results
    })
    if (data) {
      setMyPatients(data)
      const myActivePatients = data.filter((p: patientObj) => p.isActive)
      setMyActivePatients(myActivePatients)
    }
  }, [])

  const getListOfAllActivePatients = useCallback(async () => {
    setIsResultLoading(true)
    const { data } = await fetch({
      path: 'Provider/GetListOfAllActivePatients',
    })

    if (data) {
      setAllActivePatients(data)
      setIsResultLoading(false)
      return data
    }
  }, [])

  const getListOfMyActivePatients = useCallback(async () => {
    setIsResultLoading(true)
    const { data } = await fetch({
      path: 'Provider/GetListOfMyActivePatients',
    })

    if (data) {
      setMyActivePatients(data)
      setIsResultLoading(false)
      return data
    }
  }, [])

  const getListOfAllDeactivatedPatients = useCallback(async () => {
    setIsResultLoading(true)
    const { data } = await fetch({
      path: 'Provider/GetListOfAllDeactivatedPatients',
    })

    if (data) {
      setAllDeactivatedPatients(data)
      setIsResultLoading(false)
      return data
    }
  }, [])

  const getListOfMyDeactivatedPatients = useCallback(async () => {
    setIsResultLoading(true)
    const { data } = await fetch({
      path: 'Provider/GetListOfMyDeactivatedPatients',
    })

    if (data) {
      setMyDeactivatedPatients(data)
      setIsResultLoading(false)
      return data
    }
  }, [])

  useEffect(() => {
    getPatients()
  }, [])

  useEffect(() => {
    getMyPatients()
  }, [])

  // NCA-78 Call 'DeactivatePatient' service
  // Remove patient from all peer groups and set isActive status to False.
  const deactivatePatient = useCallback(
    async (patientMRN: string, deactivationDate: Date) => {
      const { error } = await fetch({
        path: `Provider/DeactivatePatient?patientMRN=${patientMRN}&deactivationDate=${
          deactivationDate.toISOString().split('T')[0]
        }`,
        methodType: 'POST',
      })
      console.log(error)
      if (error) {
        throw new Error(`Error in deactivating patient.`)
      }
    },
    []
  )

  const getPatientAccessRoles = useCallback(async () => {
    const { data } = await fetch({
      path: 'Provider/GetPatientAccessRoles',
    })

    if (data) {
      setPatientAccessRoles(data)
    }
  }, [])

  useEffect(() => {
    getPatientAccessRoles()
  }, [])

  const reactivatePatient = useCallback(async (patientMRN: string) => {
    const { error } = await fetch({
      path: `Provider/ReactivatePatient?patientMRN=${patientMRN}`,
      methodType: 'POST',
    })
    if (error) {
      throw new Error(`Error in reactivating patient.`)
    }
  }, [])

  const updatePatientAccessRole = useCallback(
    async (patientId: number, accessRoleId: number) => {
      const { error } = await fetch({
        methodType: 'POST',
        path: `Provider/UpdatePatientAccessRole?PatientId=${patientId}&AccessRoleId=${accessRoleId}`,
      })

      await getPatients()

      if (error) {
        console.log('updatePatientAccessRole-error', error)
        throw new Error(`Error in updating patient access role.`)
      }
    },
    []
  )

  return (
    <PatientsContext.Provider
      value={{
        patients,
        getPatients,
        myPatients,
        myActivePatients,
        allActivePatients,
        myDeactivatedPatients,
        allDeactivatedPatients,
        getListOfAllActivePatients,
        getListOfMyActivePatients,
        getListOfAllDeactivatedPatients,
        getListOfMyDeactivatedPatients,
        getMyPatients,
        deactivatePatient,
        reactivatePatient,
        updatePatientAccessRole,
        patientAccessRoles,
        isResultLoading,
      }}
    >
      {children}
    </PatientsContext.Provider>
  )
}

// NEW EXPORT
export const usePatients = () =>
  useContext(PatientsContext) as PatientsContextInterface
